import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  List,
  Pagination,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import CardRouter from '../cards/CardRouter';
import { useDeploy } from '../contexts/DeployContext';

/**
 * Results view for ConfigTest context.
 * Expects data from /deploy/serve/test: { served_id, num_pages, feed }.
 * Renders items as a simple list with backend pagination via /deploy/serve/page.
 * When a page response includes new_items > 0, shows an alert; clicking it loads new items (no pagination).
 */
const FeedConfigResults = ({ data }) => {
  const { deployApi } = useDeploy();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [newItemsCount, setNewItemsCount] = useState(0);
  const [showNewItemsOnly, setShowNewItemsOnly] = useState(false);
  const [newItemsList, setNewItemsList] = useState(null);
  const [newItemsLoading, setNewItemsLoading] = useState(false);

  const servedId = data?.served_id;
  const numPages = data?.num_pages ?? 0;
  const firstPageItems = data?.feed ?? [];

  useEffect(() => {
    setCurrentPage(1);
    setPageItems(null);
    setNewItemsCount(0);
    setShowNewItemsOnly(false);
    setNewItemsList(null);
  }, [servedId]);

  const loadPage = useCallback(
    async (pageNum) => {
      if (pageNum === 1) {
        setPageItems(null);
        return;
      }
      if (!servedId) return;
      setPageItems(null);
      setPageLoading(true);
      try {
        // Backend expects 0-based pages (0..num_pages-1); MUI Pagination is 1-based
        const res = await deployApi.serveFeedPage(servedId, pageNum - 1);
        const items = res?.result?.items ?? [];
        const count = res?.result?.new_items ?? 0;
        setPageItems(items);
        if (count > 0) setNewItemsCount(count);
      } catch {
        setPageItems([]);
      } finally {
        setPageLoading(false);
      }
    },
    [deployApi, servedId]
  );

  const handleShowNewItems = useCallback(async () => {
    if (!servedId) return;
    setNewItemsLoading(true);
    try {
      const res = await deployApi.serveNewItems(servedId);
      const items = res?.result?.items ?? [];
      setNewItemsList(items);
      setShowNewItemsOnly(true);
      setNewItemsCount(0);
    } catch {
      setNewItemsList([]);
    } finally {
      setNewItemsLoading(false);
    }
  }, [deployApi, servedId]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    loadPage(page);
    event?.currentTarget?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const items = currentPage === 1 ? firstPageItems : pageItems ?? [];
  const displayItems = showNewItemsOnly ? (newItemsList ?? []) : items;

  if (data === null || data === undefined) {
    return (
      <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        No results yet.
      </Box>
    );
  }

  if (!servedId || numPages === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Result list is empty
      </Typography>
    );
  }

  const paginationEl = !showNewItemsOnly && (
    <Stack direction="row" justifyContent="center" sx={{ py: 1 }}>
      <Pagination
        count={numPages}
        page={currentPage}
        onChange={handlePageChange}
        size="small"
        showFirstButton
        showLastButton
      />
    </Stack>
  );

  const isLoading = pageLoading || newItemsLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      {showNewItemsOnly && (
        <Button
          size="small"
          onClick={() => {
            setShowNewItemsOnly(false);
            setNewItemsList(null);
          }}
          sx={{ alignSelf: 'flex-start', mb: 1 }}
        >
          ← Back to feed
        </Button>
      )}
      {newItemsCount > 0 && (
        <Alert
          severity="info"
          onClick={handleShowNewItems}
          sx={{
            cursor: 'pointer',
            mb: 1,
            '&:hover': { opacity: 0.9 },
          }}
        >
          {newItemsCount} new item{newItemsCount !== 1 ? 's' : ''} available. Click to view.
        </Alert>
      )}
      {paginationEl}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <List sx={{ width: '100%', flex: 1, overflow: 'auto' }}>
          {displayItems.map((hit, idx) => (
            <CardRouter key={`${hit._index}-${hit._id}-${idx}`} hit={hit} />
          ))}
        </List>
      )}
      {paginationEl}
    </Box>
  );
};

export default FeedConfigResults;
